import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "admin" | "member" | "events_admin" | "blog_admin" | "applicant_admin" | "visit_admin";

/**
 * Checks whether the current user has a given role.
 * Note: admins automatically satisfy any role check (server-side via has_role()).
 *
 * For chapter-scoped roles (currently only `applicant_admin`), this returns true if
 * the user has the role for ANY chapter (including 'all'). For chapter-specific
 * checks, query `has_role_for_chapter` RPC directly.
 */
export const useRoleCheck = (role: AppRole) => {
  const [hasRole, setHasRole] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const check = async (userId?: string) => {
      if (!userId) {
        if (cancelled) return;
        setHasRole(false);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc("has_role", {
          _user_id: userId,
          _role: role,
        });
        if (!cancelled) setHasRole(!!data && !error);
      } catch {
        if (!cancelled) setHasRole(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      check(session?.user.id);
    }).catch(() => {
      if (!cancelled) {
        setHasRole(false);
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) =>
      setTimeout(() => check(session?.user.id), 0)
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [role]);

  return { hasRole, loading };
};
